import { useAppNavigate } from '@/hooks/use-app-navigate';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { urlParams } from '@/app/urlParams';
import { GAME_RESULT_VARIANT, GAME_SESSION_STATUS } from '@/constants/maze';
import { GameInstructionsScreen } from '@/section/instructions/instructions-screen';
import { GameInstructionsSkeleton } from '@/section/instructions/instructions-skeleton';
import { useDemoStore } from '@/store/demo';
import type { LiveGameResult } from '@/store/live';
import { useLiveStore } from '@/store/live';
import { useSettingsStore } from '@/store/settings';
import { GameSessionResponse } from '@/types/api/game';
import { getConfiguredUiStageId } from '@/utils/stage/stage-utils';
import { paths } from '@app/router/routes';

export default function HomePage() {
  const navigate = useAppNavigate();
  const stageId = getConfiguredUiStageId();
  const { isBootstrapReady, instructionContentOverride: instructionOverride } = useSettingsStore(
    useShallow((s) => ({
      isBootstrapReady: s.isBootstrapReady,
      instructionContentOverride: s.instructionContentOverride,
    })),
  );

  const {
    loadDemo,
    isLoadingDemo,
    demoSession,
    resetGame: resetDemo,
  } = useDemoStore(
    useShallow((s) => ({
      loadDemo: s.loadDemo,
      isLoadingDemo: s.isLoadingDemo,
      demoSession: s.demoSession,
      resetGame: s.resetGame,
    })),
  );

  const startLiveGame = useLiveStore((s) => s.startGame);
  const prepareLiveGameRouteEntry = useLiveStore((s) => s.prepareLiveGameRouteEntry);
  const fetchStatus = useLiveStore((s) => s.fetchStatus);
  const setResult = useLiveStore((s) => s.setResult);

  // Zitadel mode: pending until init-check resolves
  const [isRecoveryCheckPending, setIsRecoveryCheckPending] = useState(() => !!urlParams.token);

  useEffect(() => {
    if (isBootstrapReady) {
      void loadDemo();
    }
  }, [isBootstrapReady, loadDemo]);

  const getCompletedBoards = (data: GameSessionResponse, total: number): number => {
    if (typeof data.completedBoards === 'number') {
      return Math.min(Math.max(0, data.completedBoards), total);
    }

    return data.status === GAME_SESSION_STATUS.ENDED ? total : 0;
  };

  // Zitadel init-check: route based on backend session status instead of waiting for user click
  useEffect(() => {
    if (!urlParams.token || !isBootstrapReady) return;

    let cancelled = false;

    void (async () => {
      try {
        const data = await fetchStatus({ silent: true });
        if (cancelled) return;

        if (!data) {
          // No session on backend (404) — show instructions for first-time flow
          if (!cancelled) setIsRecoveryCheckPending(false);
          return;
        }

        const isTerminal =
          data.status === GAME_SESSION_STATUS.ENDED ||
          data.status === GAME_SESSION_STATUS.EXPIRED ||
          data.status === GAME_SESSION_STATUS.RESULT_PROCESSING ||
          data.status === GAME_SESSION_STATUS.MANUALLY_ENDED;

        if (isTerminal) {
          // Terminal session — build result and go to result screen
          const score = data.scoreboard.totalScore;
          const resolvedVariant =
            score === 0 ? GAME_RESULT_VARIANT.FAILURE : GAME_RESULT_VARIANT.SUCCESS;
          const total = Math.max(1, data.totalRounds);
          const completed = getCompletedBoards(data, total);
          const result: LiveGameResult = {
            stage: stageId,
            score,
            completed,
            total,
            variant: resolvedVariant,
          };
          setResult(result);
          if (!cancelled) navigate(paths.results, { replace: true });
          return;
        }

        // Active session (STARTED) — go straight to game; useLiveGameBootstrap will resume it
        if (!cancelled) navigate(paths.game, { replace: true });
      } catch {
        // Status check failed — fall back to instruction screen
        if (!cancelled) setIsRecoveryCheckPending(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isBootstrapReady, fetchStatus, navigate, setResult, stageId]);

  const handlePlayNow = async () => {
    prepareLiveGameRouteEntry();
    try {
      await startLiveGame();
      navigate(paths.game);
    } catch {
      // API errors are toasted via handleServerError in game services; stay on instructions.
    }
  };

  const handleLearnHowToPlay = () => {
    resetDemo(stageId);
    navigate(paths.demo);
  };

  const isContentLoading = !isBootstrapReady || isLoadingDemo || !demoSession || isRecoveryCheckPending;

  if (isContentLoading) {
    return <GameInstructionsSkeleton stage={stageId} />;
  }

  return (
    <GameInstructionsScreen
      stage={stageId}
      contentByStage={instructionOverride ?? undefined}
      onLearnHowToPlay={demoSession?.enableDemo === true ? handleLearnHowToPlay : undefined}
      onPlayNow={handlePlayNow}
    />
  );
}
